import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AdministrationDocumentService } from '@app/admin/services/administration-document.service';
import { DocumentService } from '@app/core/services/document.service';
import { AppActions } from '@app/store/actions';
import * as fromRoot from '@app/store/reducers';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { throwError } from 'rxjs';
import { catchError, filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';

import { DocumentsActions } from '../actions';

@Injectable()
export class DocumentEffect {
  readonly actions$ = inject(Actions);
  readonly store = inject<Store<fromRoot.State>>(Store);
  readonly documentService = inject(DocumentService);
  readonly administrationDocumentService = inject(AdministrationDocumentService);

  loadDocuments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        DocumentsActions.loadDocuments,
        DocumentsActions.uploadDocumentSuccess,
        DocumentsActions.deleteDocumentSuccess
      ),
      withLatestFrom(this.store.select(fromRoot.getCurrentUser), this.store.select(fromRoot.getCurrentLang)),
      filter(([_, currentUser, lang]) => currentUser != null && lang != null),
      switchMap(([_, currentUser, lang]) =>
        this.documentService.getUserDocuments(currentUser?.nameId, lang).pipe(
          map(documents =>
            DocumentsActions.loadDocumentsSuccess({
              documents,
            })
          ),
          catchError(error => throwError(error))
        )
      )
    )
  );

  downloadDocument$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DocumentsActions.downloadDocument),
        tap(action => {
          this.documentService.downloadDocument(action.document).pipe(takeUntilDestroyed(this._destroyRef)).subscribe();
        })
      ),
    { dispatch: false }
  );

  uploadDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentsActions.uploadDocument),
      switchMap(({ dto }) => {
        return this.administrationDocumentService
          .uploadDocument(dto)
          .pipe(map(() => DocumentsActions.uploadDocumentSuccess()));
      }),
      catchError(error => {
        this.store.dispatch(DocumentsActions.uploadDocumentFailure());
        return throwError(error);
      })
    )
  );

  uploadDocumentSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DocumentsActions.uploadDocumentSuccess),
      map(() => {
        return AppActions.httpSuccess({ messageKey: { key: 'success.upload-document' } });
      })
    );
  });

  uploadDocumentFailure$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DocumentsActions.uploadDocumentFailure),
      map(() => AppActions.httpFailure({ messageKey: { key: 'errors.upload-document' } }))
    );
  });

  deleteDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentsActions.deleteDocument),
      switchMap(({ document }) => {
        return this.administrationDocumentService
          .deleteDocument(document.id)
          .pipe(map(() => DocumentsActions.deleteDocumentSuccess()));
      })
    )
  );

  deleteDocumentSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DocumentsActions.deleteDocumentSuccess),
      map(() => {
        return AppActions.httpSuccess({ messageKey: { key: 'success.delete-document' } });
      })
    );
  });

  private _destroyRef = inject(DestroyRef);
}
